/**
 * Compresses/resizes an image file before upload.
 *
 * - Resizes longest edge to maxDim (default 1600px)
 * - Re-encodes as JPEG at given quality (default 0.85)
 * - Hardened for iOS Safari: blob → dataURL conversion is done via FileReader
 *   instead of a second canvas.toDataURL() call (which can return "data:," on
 *   memory-constrained iOS devices).
 * - Returns { base64, dataUrl, blob, file }
 *
 * Falls back to the original file when the source is unsupported (e.g. HEIC
 * without browser support) so the upload still works.
 *
 * @param {File} file - Source image file
 * @param {Object} opts - { maxDim?: number, quality?: number }
 * @returns {Promise<{base64: string, dataUrl: string, blob: Blob, file: File}>}
 */
export function compressImage(file, opts = {}) {
  const maxDim = opts.maxDim ?? 1600;
  const quality = opts.quality ?? 0.85;

  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('Dosya bulunamadı'));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Dosya okunamadı'));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => {
        // iOS may fail to decode HEIC/HEIF: fall back to original file as-is.
        const dataUrl = e.target.result;
        const base64 = typeof dataUrl === 'string' && dataUrl.includes(',')
          ? dataUrl.split(',')[1]
          : '';
        if (!base64) {
          reject(new Error('Görsel formatı desteklenmiyor'));
          return;
        }
        resolve({ base64, dataUrl, blob: file, file });
      };
      img.onload = () => {
        let { width, height } = img;
        if (!width || !height) {
          reject(new Error('Görsel boyutu okunamadı'));
          return;
        }
        const longest = Math.max(width, height);
        if (longest > maxDim) {
          const scale = maxDim / longest;
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }

        let canvas, ctx;
        try {
          canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('Canvas context oluşturulamadı');
          // White background to avoid black borders for transparent PNGs
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
        } catch (err) {
          reject(new Error('Görsel işlenemedi: ' + (err.message || err)));
          return;
        }

        const toBlobFallback = () => {
          // Some old browsers lack canvas.toBlob — use dataURL + atob fallback.
          try {
            const dataUrl = canvas.toDataURL('image/jpeg', quality);
            if (!dataUrl || !dataUrl.includes(',')) {
              reject(new Error('Sıkıştırma başarısız (boş çıktı)'));
              return;
            }
            const base64 = dataUrl.split(',')[1];
            const byteString = atob(base64);
            const ab = new ArrayBuffer(byteString.length);
            const ia = new Uint8Array(ab);
            for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
            const blob = new Blob([ab], { type: 'image/jpeg' });
            const baseName = (file.name || 'image').replace(/\.[^.]+$/, '');
            const compressedFile = new File([blob], baseName + '.jpg', { type: 'image/jpeg' });
            resolve({ base64, dataUrl, blob, file: compressedFile });
          } catch (err) {
            reject(new Error('Sıkıştırma fallback başarısız: ' + (err.message || err)));
          }
        };

        if (typeof canvas.toBlob !== 'function') {
          toBlobFallback();
          return;
        }

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              // iOS sometimes returns null when canvas is too big or memory tight
              toBlobFallback();
              return;
            }
            // Convert blob → base64/dataURL via FileReader (reliable on iOS).
            const r2 = new FileReader();
            r2.onerror = () => reject(new Error('Sıkıştırılmış görsel okunamadı'));
            r2.onload = () => {
              const dataUrl = r2.result;
              if (typeof dataUrl !== 'string' || !dataUrl.includes(',')) {
                reject(new Error('Sıkıştırma çıktısı geçersiz'));
                return;
              }
              const base64 = dataUrl.split(',')[1];
              const baseName = (file.name || 'image').replace(/\.[^.]+$/, '');
              const compressedFile = new File([blob], baseName + '.jpg', {
                type: 'image/jpeg',
              });
              resolve({ base64, dataUrl, blob, file: compressedFile });
            };
            r2.readAsDataURL(blob);
          },
          'image/jpeg',
          quality
        );
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}
